/** `tank-arena/arena-tanks` — Arena Tanks re-expressed as a composed versus
 * match: two tread-laying tanks in one open, wide, top-down arena whose
 * edges are walls. Each hull pivots in place and drives forward or back at a
 * constant speed; its turret swings on its own (B + left/right) and fires
 * shells that fly straight at the hull's own speed and ricochet off the
 * arena edge once. One hit destroys a tank and wins the round for the seat
 * that fired; a trade of hits on the same tick, or the round clock running
 * out, replays the round; the first seat to three round wins takes the match,
 * and a ten-minute match clock ends an undecided match as a draw.
 *
 * The numbers are the maintained Arena Tanks' (`maintainedTankReleaseV2.ts`
 * `MAINTAINED_TANK_ARENA_V2`, `tankLocalMatchV2.ts` rules) at half scale: its
 * 40×40 square is a 26×15 landscape arena here, so its speed-10 hull and shell move 5 units a
 * second — the same crossing times. Its hull turns 5 rad/s and its turret
 * 7.5 rad/s; a shell lives 180 ticks, bounces once, and each seat may have
 * three in the arena, 15 ticks apart; a round's clock is 1800 ticks, the
 * match is best of five and capped at 18000 ticks. Its two-seat modes (vs
 * the computer, local, online) follow from the seat relation. */
import type { GameSpecV1, ModuleUseV1 } from '../spec.js';
import type { TemplateV1, TunableV1 } from './template.js';

/** A wide landscape arena (the platform locks a landscape game to a world
 * wider than tall, and paints it a 16:9 background plate); its area is the
 * maintained 40×40 square's at half scale. */
const W = 26, H = 15;
const TANK = { w: 1, h: 1 };
/** The two hulls face each other across the arena's middle, 16 units
 * apart (the maintained arena spawns them 10 apart at full
 * scale — a quick-draw over a quarter of the arena that a pair of solver
 * bots settles in under a second; across the arena a shell takes three
 * seconds, so it can be dodged, missed and ricocheted). */
const SPAWN_INSET = 5;
const SPAWN_Y = H / 2 + TANK.h / 2;

const hull = (): ModuleUseV1[] => [
  { use: 'body.treads', config: { speed: 5, reverse: 5, turnRate: 5, startHeading: 90, blockTag: 'tank', engineCueTicks: 20 } },
  { use: 'weapon.cannon', config: { projectile: 'shell', speed: 5, cooldownTicks: 15, maxAlive: 3, muzzle: 0.5, aimRate: 7.5, startAim: 'random',
    turret: 'turret', targetTag: 'tank' } },
  // One hit destroys a tank; the round's break is its wreck pose, then both
  // tanks are back at their spawns. Nine lives outlast any first-to-five match.
  { use: 'core.vitals', config: { lives: 9, hurtTicks: 45, respawnTicks: 0, invulnTicks: 0 } },
];

const globals: ModuleUseV1[] = [
  // A shell destroys the first tank it touches — never the one that fired it.
  { use: 'rule.contactDamage', config: { attackerTag: 'shell', victimTag: 'tank', exceptAttackerTags: [], openingSafeTicks: 0, consumeAttacker: true,
    spareShooter: true } },
  // A round's clock is a minute; the break holds the wreck, then a breath.
  { use: 'flow.rounds', config: { roundTicks: 1800, breakTicks: 75, points: 1, sweepTag: 'shell', matchTicks: 18000 } },
  // Best of five: the first seat to three round wins.
  { use: 'flow.objective', config: { win: 'first-to', count: 3, milestone: 0, lose: 'players-out', requiredTag: 'required', collectTag: 'pickup', celebrate: true } },
];

export const ARENA_TANKS_SPEC: GameSpecV1 = {
  schema: 'game-spec@1', kit: 'compose@1',
  title: 'Arena Tanks',
  summary: 'a top-down two-tank arena duel with turret aiming and ricocheting cannon shells',
  world: { w: W, h: H, orientation: 'landscape' },
  players: {
    archetypes: ['tank', 'rival-tank'],
    spawns: [{ x: SPAWN_INSET, y: SPAWN_Y, facing: 1 }, { x: W - SPAWN_INSET, y: SPAWN_Y, facing: -1 }],
    relation: 'versus',
  },
  archetypes: {
    tank: {
      role: 'a compact olive-green armoured tank hull seen from above, dark track belts down both sides, an empty turret ring',
      tags: ['player', 'tank'], size: TANK, render: { palette: 4, depth: 20, anchor: 'center', facing: 'symmetric' },
      modules: hull(),
    },
    // The rival is the same tank in the other seat's colours (its art is
    // the first tank's, recoloured).
    'rival-tank': {
      role: 'a compact brick-red armoured tank hull seen from above, dark track belts down both sides, an empty turret ring',
      tags: ['player', 'tank'], size: TANK, render: { palette: 5, depth: 20, anchor: 'center', facing: 'symmetric', derivedFrom: 'tank' },
      modules: hull(),
    },
    turret: {
      role: 'a squat round gunmetal tank turret seen from above with one long straight cannon barrel reaching to its top edge',
      tags: ['turret'], size: { w: 0.8, h: 0.8 }, render: { palette: 2, depth: 25, anchor: 'center', facing: 'symmetric' },
      modules: [{ use: 'body.mount', config: { follow: 'aim' } }],
    },
    shell: {
      role: 'a small glowing orange cannon shell with a bright white-hot core',
      tags: ['shell'], size: { w: 0.25, h: 0.25 }, render: { palette: 3, depth: 30, anchor: 'center', facing: 'symmetric' },
      modules: [{ use: 'body.ricochet', config: { lifeTicks: 180, bounces: 1 } }],
    },
  },
  globals,
  hud: [{ from: 'flow.rounds', field: 'wins' }, { from: 'flow.rounds', field: 'round' }, { from: 'flow.rounds', field: 'clock' }],
  input: { profile: 'move4-fire-action', labels: { a: 'FIRE', b: 'AIM' } },
  presentation: {
    projection: 'top-down',
    backgroundRole: 'a sun-baked sandy proving ground seen from above with faint tyre ruts, scattered pebbles and dry scrub tufts',
    palette: ['#d8c08a', '#a68a5b', '#4b4f55', '#f29b2e', '#4a8f2a', '#c0392b', '#f4f1e8', '#1d1d1d'],
    backgroundPaletteIndex: 0,
  },
};

/** A hull tunable, set once and mirrored onto the rival's hull. */
const seat = (path: string, min: number, max: number, integer = false): TunableV1 =>
  ({ path: `/archetypes/tank/modules/${path}`, min, max, ...(integer ? { integer } : {}), mirrors: [`/archetypes/rival-tank/modules/${path}`] });

export const ARENA_TANKS_TEMPLATE: TemplateV1 = {
  id: 'tank-arena/arena-tanks',
  family: 'tank-arena',
  title: 'Arena Tanks',
  pitch: 'Two tanks, one walled arena: pivot and drive your hull, swing your turret, and bounce shells off the walls — one hit wins the round, first to three takes the match.',
  disclosure: {
    coreVerbs: ['drive', 'turn', 'fire'],
    mechanics: [
      'Two tanks face each other across one open, wide arena seen from above; the arena edge is a wall neither tank can leave.',
      'Left and right pivot your hull in place; up drives it forward along its heading and down backs it up, at one constant speed with no momentum. Tanks cannot drive through each other.',
      'Your turret aims on its own: hold Aim (B) and press left or right to swing it, while up and down still drive the hull. Each round the turrets start pointing in a random direction.',
      'Hold Fire to shoot along the turret: a shell every half second, at most three of yours in the arena at once. Shells fly straight at the same speed a tank drives, glance off the arena wall once, and burn out after six seconds.',
      'A shell destroys the first tank it touches, but never the tank that fired it. Destroying the rival wins the round; if both tanks are hit on the same instant, or the one-minute round clock runs out, the round is replayed and nobody scores.',
      'After each round the arena freezes for a moment on the wreck, then both tanks start the next round back at their starting spots with every shell cleared.',
    ],
    controls: 'Up/down drive forward and back; left/right pivot the hull; hold Aim (B) with left/right to swing the turret; hold Fire to shoot.',
    winCondition: 'Best of five: the first tank to win three rounds wins the match. A match still undecided after ten minutes is a draw.',
  },
  music: { mood: 'tense steady armoured standoff', genre: 'arcade chiptune', tempoBpm: 132,
    instrumentation: ['square lead', 'triangle bass', 'noise snare', 'pulse arpeggio'] },
  spec: ARENA_TANKS_SPEC,
  tunables: [
    seat('body.treads/speed', 3, 8),
    seat('body.treads/turnRate', 2.5, 8),
    seat('weapon.cannon/speed', 3, 10),
    seat('weapon.cannon/cooldownTicks', 8, 45, true),
    seat('weapon.cannon/maxAlive', 1, 5, true),
    seat('weapon.cannon/aimRate', 3, 12),
    { path: '/archetypes/shell/modules/body.ricochet/bounces', min: 0, max: 3, integer: true },
    { path: '/archetypes/shell/modules/body.ricochet/lifeTicks', min: 90, max: 300, integer: true },
    { path: '/globals/flow.rounds/roundTicks', min: 900, max: 3600, integer: true },
    { path: '/globals/flow.rounds/breakTicks', min: 45, max: 120, integer: true },
    // Round wins to take the match: best of three to best of nine.
    { path: '/globals/flow.objective/count', min: 2, max: 5, integer: true },
  ],
  contentSlots: [
    { path: '/title', maxLength: 80 },
    { path: '/summary', maxLength: 160 },
    { path: '/archetypes/*/role', maxLength: 120 },
    { path: '/presentation/backgroundRole', maxLength: 120 },
  ],
  // Every match fires, destroys tanks and wins rounds; whether a shell ever
  // glances off the wall before it hits is play, not proof.
  requirements: { events: ['fire', 'hurt', 'round-won'] },
  // The match is driven, shot, flown and scored by these: with any one
  // stubbed no round is ever won. (The turret mount is presentation only.)
  signatureModules: ['body.treads', 'weapon.cannon', 'body.ricochet', 'flow.rounds'],
  // The first tank starts facing right: up drives it right, right pivots it,
  // fire shoots.
  verbProofs: [
    { verb: 'drive', input: 'up', ms: 400, expect: { kind: 'state-change', path: 'seats.0.x' } },
    { verb: 'turn', input: 'right', ms: 300, expect: { kind: 'state-change', path: 'seats.0.angle' } },
    { verb: 'fire', input: 'a', ms: 300, expect: { kind: 'event', event: 'fire' } },
  ],
};
